import {getScrambleTextAnimation} from '@github-ui/brand-core/utils/animation'
import {XIcon} from '@primer/octicons-react'
import {Button, Heading, Link} from '@primer/react'
import {useEffect, useRef} from 'react'

import type {ProductAnnouncementModalView} from './../../ProductAnnouncement/ProductAnnouncement.types'
import ProductAnnouncementModalTabContent from './../ProductAnnouncementModalTabContent/ProductAnnouncementModalTabContent'
import ProductAnnouncementModalVisual from './../ProductAnnouncementModalVisual/ProductAnnouncementModalVisual'

import ModalStyles from './../ProductAnnouncementModal.module.css'
import Styles from './ProductAnnouncementModalMobile.module.css'

export type ProductAnnouncementModalMobileProps = ProductAnnouncementModalView

const defaultProps: Partial<ProductAnnouncementModalMobileProps> = {}

const ProductAnnouncementModalMobile = (props: ProductAnnouncementModalMobileProps) => {
  const initializedProps: ProductAnnouncementModalMobileProps = {...defaultProps, ...props}
  const {
    currentTabIndex,
    tabs,
    onTabChange,
    shouldShowTabCtas,
    shouldShowMainCta,
    seeAll,
    cta,
    title,
    onClose,
    aria,
    onEvent,
  } = initializedProps

  const currentTabIndexRef = useRef<number>(currentTabIndex)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const titleTl = useRef<gsap.core.Timeline | null>(null)

  useEffect(() => {
    if (currentTabIndexRef.current === -1 && headingRef.current) {
      titleTl.current?.kill()
      titleTl.current = getScrambleTextAnimation(headingRef.current)
    }

    currentTabIndexRef.current = currentTabIndex
  }, [currentTabIndex])

  useEffect(() => {
    return () => {
      titleTl.current?.kill()
    }
  }, [])

  return (
    <div className={Styles['ProductAnnouncementModalMobile']}>
      <div>
        <div className={Styles['ProductAnnouncementModalMobile__hero-header']}>
          <Heading
            ref={headingRef}
            className={ModalStyles['ProductAnnouncementModal__title']}
            as="h1"
            variant="medium"
            aria-label={title}
          >
            <span aria-hidden="true">{title}</span>
          </Heading>

          <button
            className={ModalStyles['ProductAnnouncementModal__closeButton']}
            onClick={onClose}
            aria-label={aria.close}
          >
            <XIcon />
          </button>
        </div>

        <div className={Styles['ProductAnnouncementModalMobile__hero-visual']}>
          <ProductAnnouncementModalVisual
            currentTabIndex={currentTabIndex}
            visuals={tabs.map(tab => tab.visual)}
            aria={aria}
          />
        </div>
      </div>

      <div className={Styles['ProductAnnouncementModalMobile__content']}>
        <dl className={Styles['ProductAnnouncementModalMobile__content-tabs']}>
          {tabs.map(({title: tabTitle, heading, ...tab}, index) => {
            const isExpanded = index === currentTabIndex
            return (
              <ProductAnnouncementModalTabContent
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`tab-${index}`}
                {...tab}
                title={heading}
                isExpanded={isExpanded}
                onAccordionToggle={() => onTabChange(index)}
                shouldShowTabCtas={!!shouldShowTabCtas}
                onEvent={(event, data = {}) => onEvent?.(event, {...data, tabName: tabTitle, tabIndex: index})}
              />
            )
          })}
        </dl>
        {seeAll && (
          <div className={Styles['ProductAnnouncementModalMobile__content-seeAll']}>
            <Link
              href={seeAll.href}
              target={seeAll.isNewWindow ? '_blank' : undefined}
              onClick={() => onEvent?.('see_all_clicked')}
            >
              {seeAll.label}
            </Link>
          </div>
        )}
      </div>

      {cta && shouldShowMainCta && (
        <div className={Styles['ProductAnnouncementModalMobile__cta']}>
          <Button
            as="a"
            href={cta.href}
            variant="primary"
            size="large"
            block
            target={cta.isNewWindow ? '_blank' : undefined}
            onClick={() => onEvent?.('main_cta_clicked')}
          >
            {cta.label}
          </Button>
        </div>
      )}
    </div>
  )
}

export default ProductAnnouncementModalMobile
